import type { SessionUser } from '@/api/profile/types';
import { getImpersonateId } from '@/lib/utils/apiFetch';

/** Root of the internal management area. */
export const MANAGEMENT_ROOT = '/management';

/** Where `/management` sends you — the tool an operator arrives to use. */
export const MANAGEMENT_DEFAULT_ROUTE = `${MANAGEMENT_ROOT}/impersonate`;

/**
 * Where `/management` sends staff who may not impersonate — CS Tools, the one
 * management tool their role is actually meant for.
 */
export const MANAGEMENT_FALLBACK_ROUTE = `${MANAGEMENT_ROOT}/cs-tools`;

const IS_PRODUCTION = import.meta.env.VITE_RETRACED_ENV === 'production';

/** Anything carrying a role — a session user, or the operator behind one. */
interface RoleBearing {
  role?: string | null | undefined;
  isRetracedStaff?: boolean | undefined;
  isRetracedAdmin?: boolean | undefined;
}

/**
 * True for a Retraced employee, from the server's IAM-native
 * `isRetracedStaff` flag — computed from actual IAM membership rather than
 * string-matching a role value that can be company-scoped (see
 * `isRetracedAdmin` below).
 *
 * Typed to the fields it reads rather than to `SessionUser`, so it also
 * accepts a `me.unImpersonatedUser` — the operator behind a customer's session.
 */
export function isRetracedStaff(user: RoleBearing | null | undefined): boolean {
  return Boolean(user?.isRetracedStaff);
}

/**
 * True while the session is borrowed from a customer.
 *
 * Two independent signals, because they can disagree: `impersonateId` is this
 * origin's own record of the switch (written when it starts, sent as a header
 * on every request), and `unImpersonatedUser` is the server's — present on
 * `/users/me` whenever the token resolves to an impersonated user. A session
 * seeded from the old dashboard's hand-off can arrive with the server side set
 * before local storage is, so neither one alone is sufficient.
 */
export function isImpersonating(user: SessionUser | null | undefined): boolean {
  return getImpersonateId() !== null || Boolean(user?.unImpersonatedUser);
}

/**
 * True for a Retraced admin specifically, from the server's IAM-native
 * `isRetracedAdmin` flag.
 *
 * Not derivable from `role`: the server fills that field from the *company
 * membership* when the request carries one, so an admin scoped to a company
 * comes back as whatever their membership in that company says. Reading
 * `role` made "is this person an admin" depend on which company they happened
 * to be looking at, which is how the management sidebar lost its Roles &
 * rights and Rules entries — the IAM-native flag is computed independently of
 * any company context.
 */
export function isRetracedAdmin(user: RoleBearing | null | undefined): boolean {
  return Boolean(user?.isRetracedAdmin);
}

/**
 * True when Retraced staff are scoped into a *customer* company.
 *
 * Reads the server's IAM-native `isActingInOwnTenant` flag alongside
 * `isRetracedStaff`: staff, with a current session company, that isn't the
 * retraced tenant itself. The flag is computed by comparing the session's
 * actual tenant id against the retraced tenant constant server-side, so
 * unlike a client-side `RETRACED_ADMIN_COMPANY_ID` comparison (env-driven,
 * defaults to empty — an unset value would quietly classify every session as
 * a customer one) it can't silently misfire.
 */
export function isScopedToCustomerCompany(
  user: SessionUser | null | undefined,
): boolean {
  return Boolean(
    user?.isRetracedStaff && user.companyId && !user.isActingInOwnTenant,
  );
}

/**
 * Impersonation is narrowed past "internal staff" in production: there it is
 * `RETRACED_ADMIN` only, everywhere else any internal role will do — so on
 * staging and locally this check always passes.
 */
export function canImpersonate(user: SessionUser | null | undefined): boolean {
  return isRetracedAdmin(user) || !IS_PRODUCTION;
}

/**
 * Gate for everything under `/management`: the visitor has to be Retraced
 * staff, acting as themselves.
 *
 * Not folded with `canImpersonate` — the management area now holds enough
 * tools (CS Tools, Accounts, Compliance, …) that staff who may not impersonate
 * still have somewhere real to land; only the impersonate page itself gates on
 * `canImpersonate`, via its own route guard and `MANAGEMENT_FALLBACK_ROUTE`.
 *
 * The "acting as themselves" half is still the important one. While
 * impersonating, the session *is* the customer's — `me` is their user, every
 * request carries their company — so management tools would read that
 * company's data and write audit entries from inside a borrowed identity.
 * Staff end the impersonation first, which is also what makes "who did this"
 * answerable afterwards.
 */
export function canAccessManagement(
  user: SessionUser | null | undefined,
): boolean {
  return isRetracedStaff(user) && !isImpersonating(user);
}

/**
 * Where an authenticated visitor belongs on arrival.
 *
 * Staff come for the management tools, so they are pointed at them rather than
 * at the customer dashboard; `/management` forwards on to whichever tool is the
 * default. Everyone else — and anyone whose `/users/me` fetch failed, so we
 * cannot tell — gets the dashboard, which is what this app did for everybody
 * before the management area moved here.
 *
 * Deliberately built on `canAccessManagement`, so a visitor is never sent
 * somewhere its own guard will bounce them straight back out of: a session
 * impersonating a customer gets the dashboard.
 */
export function postLoginRoute(
  user: SessionUser | null | undefined,
): typeof MANAGEMENT_ROOT | '/dashboard' {
  return canAccessManagement(user) ? MANAGEMENT_ROOT : '/dashboard';
}
